import { CloseOutlined } from '@ant-design/icons';
import React, { useCallback, useMemo, useState } from 'react';

import { Pair } from '../../../types/pair';
import { buildTerminalUrl } from '../../../utils/terminalUrls';
import { useSettingsStore } from '../../Settings/hooks/useSettingsStore';
import { useTimeAgo } from '../hooks/useTimeAgo';
import { calculateMigrationPercent, doesRelatedPairMatchFilters, isFilterActuallyEnabled } from '../utils/filterUtils';
import { formatFollowers, formatShortDateTime, formatTwitterHandle } from '../utils/formatters';
import ListActionButtons from './ListActionButtons';
import RelatedPairCard from './RelatedPairCard';
import TwitterIcon from './TwitterIcon';

type PairCardProps = {
  pair: Pair;
  highlighted: boolean;
  token: any;
  onHover: (isHovering: boolean) => void;
};

const stopPropagation = (e: React.MouseEvent) => {
  e.stopPropagation();
};

const normalizeAddresses = (input: string): Set<string> => {
  if (!input) {
    return new Set();
  }
  return new Set(
    input
      .split(/\r?\n/)
      .map((line) => line.trim().toLowerCase())
      .filter(Boolean)
  );
};

const PairCard: React.FC<PairCardProps> = ({ pair, highlighted, onHover }) => {
  const { filters, general, lists, setDevList } = useSettingsStore();
  const [isDevAddressHovered, setIsDevAddressHovered] = useState(false);
  const [isTwitterHovered, setIsTwitterHovered] = useState(false);
  const timeAgo = useTimeAgo(pair.createdAt);
  const migrationPercent = calculateMigrationPercent(pair.migrationCount, pair.devPairCount);
  const devWhiteList = useMemo(() => normalizeAddresses(lists.devWhiteList), [lists.devWhiteList]);
  const devBlackList = useMemo(() => normalizeAddresses(lists.devBlackList), [lists.devBlackList]);
  const isDevWhitelisted = !!pair.devAddress && devWhiteList.has(pair.devAddress.trim().toLowerCase());
  const isDevBlacklisted = !!pair.devAddress && devBlackList.has(pair.devAddress.trim().toLowerCase());
  const isTwitterWhitelisted = !!pair.twitterUsername && devWhiteList.has(pair.twitterUsername.trim().toLowerCase());
  const isTwitterBlacklisted = !!pair.twitterUsername && devBlackList.has(pair.twitterUsername.trim().toLowerCase());

  const openPairInTerminal = useCallback(
    (targetPair: Pair) => {
      if (!targetPair.tokenAddress || !targetPair.pairAddress) {
        return;
      }
      const url = buildTerminalUrl(general.terminal, targetPair.tokenAddress, targetPair.pairAddress);
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [general.terminal]
  );

  const addToDevList = useCallback(
    (listKey: 'devWhiteList' | 'devBlackList', address: string) => {
      const currentList = lists[listKey] || '';
      const addresses = currentList.split(/\r?\n/).map((line: string) => line.trim()).filter(Boolean);
      const normalizedAddress = address.trim().toLowerCase();

      // Check if address already exists (case-insensitive)
      const exists = addresses.some((addr: string) => addr.trim().toLowerCase() === normalizedAddress);
      if (!exists) {
        const newList = currentList ? `${currentList}\n${address.trim()}` : address.trim();
        setDevList(listKey, newList);
      }
    },
    [lists, setDevList]
  );

  const removeFromDevList = useCallback(
    (listKey: 'devWhiteList' | 'devBlackList', address: string) => {
      const currentList = lists[listKey] || '';
      const normalizedAddress = address.trim().toLowerCase();
      const addresses = currentList.split(/\r?\n/).map((line: string) => line.trim()).filter(Boolean);
      const filteredAddresses = addresses.filter(
        (addr: string) => addr.trim().toLowerCase() !== normalizedAddress
      );
      setDevList(listKey, filteredAddresses.join('\n'));
    },
    [lists, setDevList]
  );

  // Twitter username list handlers
  const handleTwitterBlacklist = useCallback(
    (value: string) => {
      if (isTwitterWhitelisted) {
        removeFromDevList('devWhiteList', value);
      }
      addToDevList('devBlackList', value);
    },
    [isTwitterWhitelisted, addToDevList, removeFromDevList]
  );

  const handleTwitterWhitelist = useCallback(
    (value: string) => {
      if (isTwitterBlacklisted) {
        removeFromDevList('devBlackList', value);
      }
      addToDevList('devWhiteList', value);
    },
    [isTwitterBlacklisted, addToDevList, removeFromDevList]
  );

  const handleTwitterUnWhitelist = useCallback(
    (value: string) => {
      removeFromDevList('devWhiteList', value);
    },
    [removeFromDevList]
  );

  const handleTwitterUnBlacklist = useCallback(
    (value: string) => {
      removeFromDevList('devBlackList', value);
    },
    [removeFromDevList]
  );

  return (
    <div
      className={`log-block${highlighted ? ' axiom-pair-card--highlighted' : ''}`}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      <div className="previous-tokens">
        {/* Current Token */}
        <div
          className={`token-item current-token${highlighted ? ' axiom-pair-card--highlighted' : ''}`}
          style={{ cursor: 'pointer' }}
          onDoubleClick={() => {
            if (pair.tokenAddress && pair.pairAddress) {
              openPairInTerminal(pair);
            }
          }}
        >
          {pair.tokenImage ? (
            <img
              src={pair.tokenImage}
              alt={pair.tokenTicker}
              className="token-avatar-main"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                const placeholder = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                if (placeholder && placeholder.classList.contains('token-avatar-placeholder')) {
                  placeholder.style.display = 'flex';
                }
              }}
            />
          ) : null}
          <div className={`token-avatar-placeholder${pair.tokenImage ? '' : ' token-avatar-placeholder-visible'}`}>
            <CloseOutlined />
          </div>
          <div className="token-content">
            <div className="token-item-header">
              <div className="token-name">
                {pair.tokenTicker}
                {pair.protocol && (
                  <>
                    <span className="token-protocol">[{pair.protocol}]</span>
                  </>
                )}
                {pair.tokenName && <span className="token-name-part"> - {pair.tokenName}</span>}
                {pair.tokenAddress && pair.pairAddress && (
                  <button
                    type="button"
                    className="token-open-terminal-link"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      openPairInTerminal(pair);
                    }}
                  >
                    Open terminal
                  </button>
                )}
              </div>
              <div
                className="token-address"
                style={{ cursor: 'text' }}
                onDoubleClick={stopPropagation}
              >
                {pair.pairAddress || 'N/A'}
              </div>
            </div>
            <div className="token-details-wrapper">
              <div className="token-details-compact">
                {pair.twitterUsername && (
                  <div
                    className="detail-line detail-line--twitter"
                    onMouseEnter={() => setIsTwitterHovered(true)}
                    onMouseLeave={() => setIsTwitterHovered(false)}
                  >
                    <span className="detail-label detail-label--twitter">
                      <TwitterIcon />
                      Twitter:
                    </span>
                    <span className="detail-value">
                      <span className={`twitter-value-wrapper${isTwitterWhitelisted ? ' twitter-value-wrapper--whitelisted' : ''}`}>
                        {pair.twitter ? (
                          <a className="twitter-link" href={pair.twitter} target="_blank" rel="noopener noreferrer">
                            {formatTwitterHandle(pair.twitterUsername)}
                          </a>
                        ) : (
                          formatTwitterHandle(pair.twitterUsername)
                        )}
                        {pair.twitterFollowers !== undefined && pair.twitterFollowers !== null && (
                          <span className="twitter-followers">
                            {' '}
                            ({formatFollowers(pair.twitterFollowers)})
                          </span>
                        )}
                        <ListActionButtons
                          value={pair.twitterUsername}
                          isWhitelisted={isTwitterWhitelisted}
                          isBlacklisted={isTwitterBlacklisted}
                          onWhitelist={handleTwitterWhitelist}
                          onBlacklist={handleTwitterBlacklist}
                          onUnWhitelist={handleTwitterUnWhitelist}
                          onUnBlacklist={handleTwitterUnBlacklist}
                          isVisible={isTwitterHovered}
                        />
                      </span>
                    </span>
                  </div>
                )}
                {pair.devAddress && (
                  <div
                    className="detail-line detail-line--dev"
                    onMouseEnter={() => setIsDevAddressHovered(true)}
                    onMouseLeave={() => setIsDevAddressHovered(false)}
                  >
                    <span className="detail-label">Dev:</span>
                    <span
                      className="detail-value dev-address-wrapper"
                      style={{ cursor: 'text' }}
                      onDoubleClick={stopPropagation}
                    >
                      <span className={`dev-address-tag dev-address-tag-wrapper${isDevWhitelisted ? ' dev-address-tag--whitelisted' : ''}`}>
                        {pair.devAddress}
                        <ListActionButtons
                          value={pair.devAddress}
                          isWhitelisted={isDevWhitelisted}
                          isBlacklisted={isDevBlacklisted}
                          onWhitelist={(value) => {
                            if (isDevBlacklisted) {
                              removeFromDevList('devBlackList', value);
                            }
                            addToDevList('devWhiteList', value);
                          }}
                          onBlacklist={(value) => {
                            if (isDevWhitelisted) {
                              removeFromDevList('devWhiteList', value);
                            }
                            addToDevList('devBlackList', value);
                          }}
                          onUnWhitelist={(value) => removeFromDevList('devWhiteList', value)}
                          onUnBlacklist={(value) => removeFromDevList('devBlackList', value)}
                          isVisible={isDevAddressHovered}
                        />
                      </span>
                      {pair.devPairCount !== undefined && pair.devPairCount !== null && (
                        <span className="token-date-time" style={{ display: 'inline' }}> (pairs: {pair.devPairCount})</span>
                      )}
                    </span>
                  </div>
                )}
                {migrationPercent !== null && pair.devPairCount !== undefined && pair.devPairCount !== null && (
                  <div className="detail-line">
                    <span className="detail-label">Migrated:</span>
                    <span className="detail-value">
                      {migrationPercent.toFixed(2)}% ({pair.migrationCount}/{pair.devPairCount})
                    </span>
                  </div>
                )}
              </div>
              {pair.createdAt && (
                <div className="token-date-time">
                  {formatShortDateTime(pair.createdAt) || ''}
                  <br />
                  {timeAgo}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Related Pairs */}
        {pair.relatedPairs && pair.relatedPairs.length > 0 && (
          <div>
            {pair.relatedPairs.map((relatedPair, index) => {
              // Only highlight related pairs if there are related pair filters enabled
              // Migration % is checked on main pair, not related pairs
              const hasRelatedPairFilters = isFilterActuallyEnabled(filters.motionPercent) ||
                isFilterActuallyEnabled(filters.bmotionPercent) || isFilterActuallyEnabled(filters.maxMCPercent) ||
                isFilterActuallyEnabled(filters.openPrice) || isFilterActuallyEnabled(filters.totalPairFees) || filters.motionGreaterBMotion.enabled;
              const shouldHighlight = hasRelatedPairFilters && doesRelatedPairMatchFilters(relatedPair, {
                motionPercent: filters.motionPercent,
                bmotionPercent: filters.bmotionPercent,
                maxMCPercent: filters.maxMCPercent,
                openPrice: filters.openPrice,
                totalPairFees: filters.totalPairFees,
                migrationPercent: filters.migrationPercent,
                motionGreaterBMotion: filters.motionGreaterBMotion,
              });

              return (
                <RelatedPairCard
                  key={relatedPair.id}
                  pair={relatedPair}
                  index={index}
                  highlighted={shouldHighlight}
                  onOpenTerminal={openPairInTerminal}
                  mainPairTwitterUsername={pair.twitterUsername}
                  mainPairDevAddress={pair.devAddress}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PairCard;
